import React from 'react';
import { Box } from '@mui/material';
import {
  buildFunnelPath,
  FUNNEL_CURVES,
  getMonthTicks,
  GraphData,
  SCHEDULE_GRAPH_LAYOUT,
  SCHEDULE_MAX_PERCENT,
  SVG_COLORS,
} from '../utils/coverPageGraphs.util';

interface CoverPageGraphsProps {
  graphData: GraphData;
  hoveredFundingId?: string | null;
}

const ScheduleGraph: React.FC<{ scheduleData: NonNullable<GraphData['scheduleData']>; hoveredFundingId?: string | null }> = ({
  scheduleData,
  hoveredFundingId,
}) => {
  const { maxMonths, actualProgress } = scheduleData;
  const { primaryBlue, gridColor, textColor, lightText } = SVG_COLORS;
  // Same geometry, fonts and strokes as renderScheduleGraph so this preview matches the exported report.
  const { width: chartWidth, height: chartHeight, padding } = SCHEDULE_GRAPH_LAYOUT;
  const plotWidth = chartWidth - padding * 2;
  const plotHeight = chartHeight - padding * 2;

  const scaleX = (month: number) => padding + (month / maxMonths) * plotWidth;
  const scaleY = (percent: number) => padding + plotHeight - (Math.min(percent, SCHEDULE_MAX_PERCENT) / SCHEDULE_MAX_PERCENT) * plotHeight;

  const actualPath = actualProgress.map((p, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(p.month)} ${scaleY(p.percent)}`).join(' ');

  const monthTicks = getMonthTicks(maxMonths);
  const percentTicks = Array.from({ length: SCHEDULE_MAX_PERCENT / 20 + 1 }, (_, i) => i * 20);

  return (
    <svg width={chartWidth} height={chartHeight} xmlns='http://www.w3.org/2000/svg' style={{ backgroundColor: 'white' }}>
      <text x={0} y={20} fontFamily='Times New Roman, serif' fontSize={16} fontWeight={600} fill={textColor}>
        Funding Schedule
      </text>

      {percentTicks.map(p => (
        <line
          key={p}
          x1={padding}
          y1={scaleY(p)}
          x2={padding + plotWidth}
          y2={scaleY(p)}
          stroke={gridColor}
          strokeWidth={1}
          strokeDasharray='2,2'
        />
      ))}

      {/* Square frame around the plot area so the end of the axis reads as the end of the schedule. */}
      <rect x={padding} y={padding} width={plotWidth} height={plotHeight} fill='none' stroke={textColor} strokeWidth={1} />

      <path
        d={buildFunnelPath(FUNNEL_CURVES.upper.fn, padding, plotWidth, plotHeight, FUNNEL_CURVES.upper.xStart)}
        stroke={lightText}
        strokeWidth={1.5}
        strokeDasharray='5,4'
        fill='none'
      />
      <path
        d={buildFunnelPath(FUNNEL_CURVES.lower.fn, padding, plotWidth, plotHeight, FUNNEL_CURVES.lower.xStart)}
        stroke={lightText}
        strokeWidth={1.5}
        strokeDasharray='5,4'
        fill='none'
      />

      <text
        x={padding + 10}
        y={padding + 15}
        fontFamily='Times New Roman, serif'
        fontSize={10}
        fill={textColor}
        fontWeight={500}
        opacity={0.7}
      >
        Ahead of Schedule
      </text>
      <text
        x={padding + plotWidth - 95}
        y={padding + plotHeight - 10}
        fontFamily='Times New Roman, serif'
        fontSize={10}
        fill={textColor}
        fontWeight={500}
        opacity={0.7}
      >
        Behind Schedule
      </text>

      <path d={actualPath} stroke={primaryBlue} strokeWidth={3} fill='none' strokeLinecap='round' />
      {actualProgress.map((p, i) => {
        const isHovered = hoveredFundingId != null && p.id === hoveredFundingId;
        return (
          <circle
            key={p.id ?? i}
            cx={scaleX(p.month)}
            cy={scaleY(p.percent)}
            r={isHovered ? 7 : 5}
            fill={isHovered ? '#f57c00' : primaryBlue}
            stroke='white'
            strokeWidth={2}
          />
        );
      })}

      <text
        x={15}
        y={chartHeight / 2}
        fontFamily='Times New Roman, serif'
        fontSize={11}
        fill={textColor}
        textAnchor='middle'
        transform={`rotate(-90, 15, ${chartHeight / 2})`}
      >
        % Funding
      </text>
      <text x={chartWidth / 2} y={chartHeight - 8} fontFamily='Times New Roman, serif' fontSize={11} fill={textColor} textAnchor='middle'>
        Months
      </text>

      {percentTicks.map(p => (
        <text key={p} x={padding - 8} y={scaleY(p) + 4} fontFamily='Times New Roman, serif' fontSize={9} fill={lightText} textAnchor='end'>
          {p}%
        </text>
      ))}
      {monthTicks.map(m => (
        <React.Fragment key={m}>
          <line x1={scaleX(m)} y1={padding + plotHeight} x2={scaleX(m)} y2={padding + plotHeight + 4} stroke={textColor} strokeWidth={1} />
          <text x={scaleX(m)} y={chartHeight - 20} fontFamily='Times New Roman, serif' fontSize={9} fill={lightText} textAnchor='middle'>
            {m}
          </text>
        </React.Fragment>
      ))}
    </svg>
  );
};

// graphData.reallocationData is prepared by prepareGraphData but not rendered here —
// it is consumed by renderReallocationGraph in the PDF export path only.
const CoverPageGraphs: React.FC<CoverPageGraphsProps> = ({ graphData, hoveredFundingId }) => {
  if (!graphData.scheduleData) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <ScheduleGraph scheduleData={graphData.scheduleData} hoveredFundingId={hoveredFundingId} />
    </Box>
  );
};

export default CoverPageGraphs;
